import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

const RELEASE_SHA = import.meta.env.VITE_RELEASE_SHA

async function fetchDeployedSha() {
  const res = await fetch('/version.json', { cache: 'no-store' })
  if (!res.ok) throw new Error(`version.json returned ${res.status}`)
  return ((await res.json()) as { sha: string }).sha
}

/**
 * Reloads the page on the next navigation once a newer release is deployed,
 * so long-lived tabs don't keep running a stale bundle against a newer API.
 */
export function ReloadOnNewRelease() {
  const { pathname } = useLocation()
  const { data: deployedSha } = useQuery({
    queryKey: ['release'],
    queryFn: fetchDeployedSha,
    enabled: !!RELEASE_SHA,
    refetchInterval: 10 * 60_000,
    // The client's 5-minute staleTime would otherwise skip refetches on focus
    refetchOnWindowFocus: 'always',
  })
  const stale = !!deployedSha && deployedSha !== RELEASE_SHA

  // biome-ignore lint/correctness/useExhaustiveDependencies: runs on navigation only, not when `stale` flips, so a half-filled form isn't wiped
  useEffect(() => {
    if (stale) window.location.reload()
  }, [pathname])

  return null
}

/** Error boundary fallback that reloads when the crash may come from running a stale release. */
export function ReloadOnNewReleaseFallback() {
  useEffect(() => {
    if (!RELEASE_SHA) return
    fetchDeployedSha()
      .then((sha) => {
        // Once per release: if the reload still serves the old bundle, it would crash and reload forever
        const key = `reloaded-for-${sha}`
        if (sha === RELEASE_SHA || sessionStorage.getItem(key)) return
        sessionStorage.setItem(key, '1')
        window.location.reload()
      })
      .catch(() => {})
  }, [])

  return <p>Something went wrong.</p>
}
